import React, { useEffect, useState } from "react";
import { Heading, Notification, Table, Text } from "@telia/teddy";
import { API_URL_RETAILER_PRESENTATION_DOWNLOAD, API_URL_RETAILER_PRESENTATIONS } from "../../api";

type DocumentType = "PROVISJONSBULLETIN" | "PRESENTASJON" | "SKJEMA" | "RUTINER";

type Document = {
    id: string;
    title: string;
    ingress: string;
    fileName: string;
    startDate: string;
    updatedAt: string;
    chain: "ELKJOP" | "POWER" | "D2D" | "EXTERNAL_RETAIL" | "RETAIL_AND_D2D" | "TM_INTERNAL" | "TM_EXTERNAL" | null;
    documentType: DocumentType;
};

const DOCUMENT_LABELS: Record<DocumentType, string> = {
    PROVISJONSBULLETIN: "Provisjonsbulletin",
    PRESENTASJON: "Presentasjoner",
    SKJEMA: "Skjema",
    RUTINER: "Rutiner",
};

interface DocumentListProps {
    documentType: DocumentType;
}

const formatDate = (iso: string): string => new Date(iso).toLocaleDateString("nb-NO");

const DocumentList: React.FC<DocumentListProps> = ({ documentType }) => {
    const [documents, setDocuments] = useState<Document[]>([]);
    const [loading, setLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    const title = DOCUMENT_LABELS[documentType];

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setHasError(false);

        fetch(API_URL_RETAILER_PRESENTATIONS)
            .then((res) => {
                if (!res.ok) throw new Error(`Serverfeil: ${res.status}`);
                return res.json() as Promise<Document[]>;
            })
            .then((data) => {
                if (cancelled) return;
                setDocuments(data);
            })
            .catch(() => {
                if (cancelled) return;
                setHasError(true);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, []);

    const visible = documents.filter((d) => d.documentType === documentType);

    return (
        <div className="retailer-dashboard-wrapper">
            <Heading as="h1" variant="title-300" mb="400">
                {title}
            </Heading>

            {loading && <Text>Laster...</Text>}

            {!loading && hasError && (
                <Notification type="tile" variant="error">
                    <Notification.Icon />
                    <Notification.Text>Kunne ikke hente {title.toLowerCase()}. Prøv igjen senere.</Notification.Text>
                </Notification>
            )}

            {!loading && !hasError && visible.length === 0 && <Text>Ingen {title.toLowerCase()} foreløpig.</Text>}

            {!loading && !hasError && visible.length > 0 && (
                <Table>
                    <Table.Header>
                        <Table.Row>
                            <Table.Head>Beskrivelse</Table.Head>
                            <Table.Head width="170px">Dato sist endret</Table.Head>
                            <Table.Head width="110px"></Table.Head>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {visible.map((doc) => (
                            <Table.Row key={doc.id}>
                                <Table.Cell>{doc.ingress}</Table.Cell>
                                <Table.Cell>
                                    <Text>{formatDate(doc.updatedAt)}</Text>
                                </Table.Cell>
                                <Table.Cell>
                                    <a href={API_URL_RETAILER_PRESENTATION_DOWNLOAD(doc.id)} download={doc.fileName}>
                                        Last ned
                                    </a>
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            )}
        </div>
    );
};

export default DocumentList;
